import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../services/api';
import { useI18n } from '../../contexts/I18nContext';
import './ProfileForm.scss';

const ProfileForm = ({ userProfile, onProfileUpdate }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: userProfile?.name || '',
    email: userProfile?.email || ''
  });
  const [originalData, setOriginalData] = useState({
    name: userProfile?.name || '',
    email: userProfile?.email || ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 检测是否有变化
  const hasChanges = useMemo(() => {
    return formData.name !== originalData.name || formData.email !== originalData.email;
  }, [formData, originalData]);

  // 当用户数据更新时，重置表单
  useEffect(() => {
    if (userProfile) {
      const userData = {
        name: userProfile.name || '',
        email: userProfile.email || ''
      };
      setFormData(userData);
      setOriginalData(userData);
    }
  }, [userProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    setError('');
    setSuccess('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Username cannot be empty');
      return false;
    }
    
    if (formData.name.trim().length < 2) {
      setError('Username must be at least 2 characters');
      return false;
    }
    
    if (formData.name.trim().length > 50) {
      setError('Username cannot exceed 50 characters');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!hasChanges) {
      setError(t('profile.noChangesDetected'));
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.put('/users/me', {
        name: formData.name.trim()
      });

      const updatedUser = response.data;
      
      setSuccess(t('profile.profileUpdatedSuccessfully'));
      
      // Update original data to reflect the saved state
      setOriginalData({
        name: formData.name.trim(),
        email: formData.email
      });
      
      // Notify parent component that user info has been updated
      if (onProfileUpdate) {
        onProfileUpdate(updatedUser);
      }

    } catch (error) {
      console.error('Profile update error:', error);
      setError(error.response?.data?.message || error.message || t('profile.updateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    const userData = {
      name: userProfile?.name || '',
      email: userProfile?.email || ''
    };
    setFormData(userData);
    setError('');
    setSuccess('');
  };

  return (
    <div className="profile-form">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name" className="form-label">
              {t('profile.username')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('profile.enterUsername')}
              className={`form-input ${error ? 'error' : ''}`}
              disabled={isLoading}
            />
          <div className="input-hint">
            {formData.name.length}/50 {t('profile.characters')}
          </div>
        </div>

        {error && (
          <div className="message error-message">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1Z" fill="#dc3545"/>
              <path d="M8 4V8M8 10H8.01" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {error}
          </div>
        )}

        {success && (
          <div className="message success-message">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1Z" fill="#28a745"/>
              <path d="M5 8L7 10L11 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {success}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="btn btn-secondary"
            disabled={isLoading || !hasChanges}
          >
            {t('common.reset')}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading || !hasChanges}
          >
            {isLoading ? t('profile.updating') : t('profile.saveChanges')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;